import type { CharacterProfile } from '../types';

export interface CampaignIdentity {
  seed: number;
  campaignNumber: number;
}

export interface PlannedMatchup {
  leftCharacterId: string;
  rightCharacterId: string;
  cameoCharacterId?: string;
  matchupKey: string;
}

const STORAGE_KEY = 'smackers-viewer-campaign-v1';
const VERSUS_SLOTS_PER_CAMPAIGN = 7;

function hash(seed: number, value: string) {
  let result = (2166136261 ^ seed) >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function random(seed: number) {
  let value = seed >>> 0;
  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffled<T>(items: T[], seed: number) {
  const result = [...items];
  const next = random(seed);
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(next() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

function freshSeed() {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] || 0x534d4143;
}

export function loadCampaignIdentity(): CampaignIdentity {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<CampaignIdentity> | null;
    if (saved && Number.isInteger(saved.seed) && Number.isInteger(saved.campaignNumber)) {
      return { seed: saved.seed as number, campaignNumber: Math.max(0, saved.campaignNumber as number) };
    }
  } catch {
    // Storage may be unavailable in hardened browsing modes.
  }
  const identity = { seed: freshSeed(), campaignNumber: 0 };
  saveCampaignIdentity(identity);
  return identity;
}

export function saveCampaignIdentity(identity: CampaignIdentity) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(identity)); } catch { /* continue with in-memory identity */ }
}

export function nextCampaignIdentity(identity: CampaignIdentity) {
  const next = { ...identity, campaignNumber: identity.campaignNumber + 1 };
  saveCampaignIdentity(next);
  return next;
}

export function profileIdForSlot(characterIds: string[], identity: CampaignIdentity, localSlot: number) {
  if (characterIds.length === 0) throw new RangeError('Cannot plan profiles for an empty roster.');
  if (!Number.isInteger(localSlot) || localSlot < 0 || localSlot >= characterIds.length) {
    throw new RangeError(`Profile slot ${localSlot} is outside this campaign's roster.`);
  }
  const bag = shuffled(characterIds, hash(identity.seed, `profile-campaign:${identity.campaignNumber}`));
  return bag[localSlot];
}

export function matchupForSlot(characters: CharacterProfile[], identity: CampaignIdentity, localSlot: number): PlannedMatchup {
  const pairs: Array<[string, string]> = [];
  for (let left = 0; left < characters.length; left += 1) {
    for (let right = left + 1; right < characters.length; right += 1) {
      pairs.push([characters[left].id, characters[right].id]);
    }
  }
  const globalSlot = identity.campaignNumber * VERSUS_SLOTS_PER_CAMPAIGN + localSlot;
  const pass = Math.floor(globalSlot / pairs.length);
  const pairIndex = globalSlot % pairs.length;
  const pair = shuffled(pairs, hash(identity.seed, `matchup-pass:${pass}`))[pairIndex];
  const swapSides = random(hash(identity.seed, `sides:${globalSlot}`))() > 0.5;
  const [leftCharacterId, rightCharacterId] = swapSides ? [pair[1], pair[0]] : pair;
  const bossEncounter = characters.some((character) => character.isBoss && pair.includes(character.id));
  const cameoRoster = characters.filter((character) => !character.isBoss);
  const cameoIndex = Math.floor(random(hash(identity.seed, `cameo:${globalSlot}`))() * cameoRoster.length);
  return {
    leftCharacterId,
    rightCharacterId,
    cameoCharacterId: bossEncounter ? cameoRoster[cameoIndex]?.id : undefined,
    matchupKey: [...pair].sort().join('--'),
  };
}

export const CAMPAIGN_PLANNER_LIMITS = {
  versusSlotsPerCampaign: VERSUS_SLOTS_PER_CAMPAIGN,
  campaignStorageKey: STORAGE_KEY,
} as const;
